<template>
  <div>
    <v-dialog
      v-model="display"
      :width="width"
      persistent
      lazy
      full-width
      :disabled="disabled"
    >
      <v-card>
        <v-card-text style="padding: 0; height: 376px">
          <v-date-picker
            v-model="datePart"
            :locale="isAdminLocalization ? $adminLocale() : locale"
            :min="min ? getDate(min) : undefined"
            :max="max ? getDate(max) : undefined"
            full-width
            scrollable
            actions
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <main-button :text="cancelText === 'Cancel' ? adminText('shared.cancel', 'Cancel') : cancelText" flat @click.native="cancelHandler" />
          <main-button :text="okText === 'OK' ? adminText('shared.ok', 'OK') : okText" depressed @click.native="okHandler" />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-text-field
      :label="label"
      :value="manualInput ? manualDateValue : formattedDatetime"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="!manualInput"
      box
      :error-messages="getErrors(validator)"
      append-icon="event"
      @input="onManualInput"
      @click="!manualInput && (display = true)"
      @click:append="display = true"
      @blur="validator && validator.$touch()"
    />
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import moment from 'moment'
import { defaultMessages, getValidationErrors } from './../../../utils'

const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD'
const CROATIAN_DATE_FORMAT = 'DD.MM.YYYY.'

export default {
  mixins: [AdminContext],
  name: 'date-field',
  props: {
    validator: {
      type: Object,
      default: null,
    },
    errorMessages: {
      type: Object,
      default: null,
    },
    value: {
      type: [Date, String],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    width: {
      type: Number,
      default: 320,
    },
    format: {
      type: String,
      default: 'dddd, MMMM Do YYYY',
    },
    outputFormat: {
      type: String,
      default: DEFAULT_DATE_FORMAT,
    },
    placeholder: {
      type: String,
      default: '',
    },
    manualInput: {
      type: Boolean,
      default: false,
    },
    locale: {
      type: String,
      default: 'en-us',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    okText: {
      type: String,
      default: 'OK',
    },
    min: {
      type: String,
      default: undefined,
    },
    max: {
      type: String,
      default: undefined,
    },
  },
  data() {
    if (this.value) {
      const initialDate = moment(this.value)
      this.$emit(
        'input',
        this.outputFormat === DEFAULT_DATE_FORMAT
          ? new Date(this.value)
          : initialDate.format(this.outputFormat),
      )
    }

    return {
      display: false,
      timeSelected: false,
      selectedDatetime: this.value,
      manualDateValue: this.formatDateValue(this.value),
    }
  },
  computed: {
    datePart: {
      get() {
        const date = this.selectedDatetime
          ? new Date(this.selectedDatetime)
          : new Date(this.min || this.max || new Date().getTime())

        return moment(date).format(DEFAULT_DATE_FORMAT)
      },
      set(val) {
        let date = moment(val, DEFAULT_DATE_FORMAT)
        let input = moment().year(date.year()).month(date.month()).date(date.date())
        this.selectedDatetime = input.toDate()
      },
    },
    formattedDatetime() {
      return this.formatDateValue(this.value)
    },
  },
  watch: {
    value(value) {
      this.selectedDatetime = value
      const parsedValue = moment(value, [DEFAULT_DATE_FORMAT, CROATIAN_DATE_FORMAT, 'DD.MM.YYYY'], true)

      if (!this.manualInput || parsedValue.isValid()) {
        this.manualDateValue = this.formatDateValue(value)
      }
    },
    display(val) {
      if (val) {
        this.selectedDatetime = this.selectedDatetime
          ? this.selectedDatetime
          : new Date(this.min || this.max || new Date().getTime())
      }
    },
  },
  methods: {
    getDate(input) {
      return moment(input).format(DEFAULT_DATE_FORMAT)
    },
    formatDateValue(value) {
      if (!value) return ''

      const parsedValue = moment(value, [DEFAULT_DATE_FORMAT, CROATIAN_DATE_FORMAT, 'DD.MM.YYYY'], true)

      return parsedValue.isValid() ? parsedValue.format(this.format) : ''
    },
    onManualInput(value) {
      if (!this.manualInput) return

      const digits = String(value || '')
        .replace(/\D/g, '')
        .slice(0, 8)
      const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean)
      const formattedValue = parts.join('.') + (digits.length === 8 ? '.' : '')
      const parsedValue = moment(formattedValue, CROATIAN_DATE_FORMAT, true)

      this.manualDateValue = formattedValue
      this.$emit('input', parsedValue.isValid() ? parsedValue.format(this.outputFormat) : formattedValue)
    },
    okHandler() {
      this.display = false
      this.selectedDatetime = this.selectedDatetime
        ? this.selectedDatetime
        : this.min || this.max || new Date()
      this.manualDateValue = moment(this.selectedDatetime).format(this.format)
      this.$emit('input', moment(this.selectedDatetime).format(this.outputFormat))
    },
    getErrors: function (field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },
    cancelHandler() {
      this.display = false
    },
  },
}
</script>

<style>
.v-time-picker-title {
  height: 56px !important;
}
.v-input--has-state .v-input__slot:before {
  border-color: rgb(219, 56, 76) !important;
}
</style>

<style scoped>
.v-card {
  box-shadow: none;
}
</style>
